import { ApiClient } from "../../api/ApiClient";
import React, { useEffect, useState, useMemo } from "react";
import { Modal, Table, Tabs, TabPane, Tag, Descriptions, Typography } from "@douyinfe/semi-ui";
import { formatAmountZhCn } from "../../utils";

const apiClient = ApiClient.getDefault();
const { Text } = Typography;

export interface MonthDrillDownProps {
  visible: boolean;
  yearMonth: string;
  paidMethod: string;
  reportType: string;
  leadId?: number;
  onClose: () => void;
}

interface DailyData {
  dueDate: string;
  dayOfWeek: string;
  totalUnpaid: number;
  totalPaid: number;
  mortgageUnpaid: number;
  cardUnpaid: number;
  childList: DailyChild[];
}

interface DailyChild {
  leadId: number;
  leadName: string;
  platform: string;
  oneTermAmount: number;
  status: string;
  loanType: string;
  paidMethod: string;
}

export default function MonthDrillDownModal(props: MonthDrillDownProps) {
  const [data, setData] = useState<DailyData[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (props.visible && props.yearMonth) {
      setLoading(true);
      apiClient.request("/mortgageLoanReport/dailyDetail", "GET", {
        yearMonth: props.yearMonth,
        paidMethod: props.paidMethod,
        leadId: props.leadId,
      }).then((resp: any) => {
        setData(resp.data || []);
        setLoading(false);
      }).catch(() => setLoading(false));
    }
  }, [props.visible, props.yearMonth, props.paidMethod, props.leadId]);

  // 摘要统计
  const summary = useMemo(() => {
    let totalUnpaid = 0, totalPaid = 0, maxDay = "", maxAmount = 0;
    data.forEach(d => {
      totalUnpaid += d.totalUnpaid || 0;
      totalPaid += d.totalPaid || 0;
      if ((d.totalUnpaid || 0) > maxAmount) {
        maxAmount = d.totalUnpaid || 0;
        maxDay = d.dueDate;
      }
    });
    const days = data.length || 1;
    return { totalUnpaid, totalPaid, maxDay, maxAmount, avgUnpaid: totalUnpaid / days };
  }, [data]);

  // 解析月份显示
  const monthLabel = props.yearMonth ? `${props.yearMonth.split("-")[1]}月` : "";
  const paidLabel = props.paidMethod === "DH" ? "代还" : "自还";

  // ====== 表格视图 ======
  const tableColumns: any[] = [
    {
      title: "日期",
      dataIndex: "dueDate",
      width: 120,
      render: (v: string, r: DailyData) =>{
        if(v=="汇总"){
            return (
              <Typography.Title heading={4}>
                {v}
              </Typography.Title>
            )
        } else {
            return (
        <span style={{ fontWeight: 600 }}>
          {v?.substring(5)}({r.dayOfWeek})
        </span>
         )
        }
      } 
    },
    {
      title: "总待还",
      dataIndex: "totalUnpaid",
      width: 100,
      render: (v: number) => <Text style={{ color: v > 0 ? '#e91e63' : '#999', fontWeight: 600 }}>{formatAmountZhCn(v)}</Text>,
    },
    {
      title: "总已还",
      dataIndex: "totalPaid",
      width: 100,
      render: (v: number) => <Text style={{ color: v > 0 ? '#00b578' : '#999' }}>{formatAmountZhCn(v)}</Text>,
    },
    {
      title: "贷款待还",
      dataIndex: "mortgageUnpaid",
      width: 100,
      render: (v: number) => <Text>{formatAmountZhCn(v)}</Text>,
    },
    {
      title: "信用卡待还",
      dataIndex: "cardUnpaid",
      width: 100,
      render: (v: number) => <Text>{formatAmountZhCn(v)}</Text>,
    },
  ];

  const renderExpandedRow = (record: DailyData) => {
    if (!record.childList || record.childList.length === 0) return <Text type="tertiary">暂无明细</Text>;
    const childCols: any[] = [
      { title: "客户", dataIndex: "leadName", width: 100 },
      { title: "平台", dataIndex: "platform", width: 140 },
      {
        title: "月供", dataIndex: "oneTermAmount", width: 90,
        render: (v: number) => formatAmountZhCn(v),
      },
      {
        title: "状态", dataIndex: "status", width: 70,
        render: (v: string) => v === 'HKZ' ? <Tag color="red" size="small">待还</Tag> : <Tag color="green" size="small">已还</Tag>,
      },
      {
        title: "类型", dataIndex: "loanType", width: 70,
        render: (v: string) => v === 'MORTGAGE' ? '贷款' : v === 'CARD' ? '信用卡' : '借款',
      },
    ];
    return (
      <div style={{ paddingLeft: 24, borderLeft: '3px solid #1677ff', marginLeft: 10 }}>
        <Table columns={childCols} dataSource={record.childList} rowKey="leadId" size="small" pagination={false} />
      </div>
    );
  };

  // ====== 日历视图 ======
  const renderCalendar = () => {
    if (!props.yearMonth) return null;
    const [yearStr, monthStr] = props.yearMonth.split("-");
    const year = parseInt(yearStr);
    const month = parseInt(monthStr);
    const firstDay = new Date(year, month - 1, 1);
    const lastDate = new Date(year, month, 0).getDate();
    // 0=Sun, 1=Mon... 转为 Mon=0
    let startDow = firstDay.getDay();
    startDow = startDow === 0 ? 6 : startDow - 1;

    const dataMap = new Map<string, DailyData>();
    data.forEach(d => dataMap.set(d.dueDate, d));

    const weekDays = ["一", "二", "三", "四", "五", "六", "日"];
    const cells: React.ReactNode[] = [];

    // header
    cells.push(...weekDays.map(w => (
      <div key={w} style={{ textAlign: 'center', fontWeight: 600, fontSize: 16, color: '#999', padding: '4px 0' }}>{w}</div>
    )));

    // empty cells before first day
    for (let i = 0; i < startDow; i++) {
      cells.push(<div key={`e${i}`} style={{textAlign: 'center', minHeight: 56, background: '#fafafa' }} />);
    }

    for (let d = 1; d <= lastDate; d++) {
      const dateStr = `${year}-${String(month).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
      const dayData = dataMap.get(dateStr);
      const unpaid = dayData?.totalUnpaid || 0;
      const paid = dayData?.totalPaid || 0;
      const hasData = dayData != null;
      const isToday = dateStr === new Date().toISOString().substring(0, 10);

      cells.push(
        <div key={d} style={{
          minHeight: 56, padding: '3px 5px', borderRight: '1px solid #f0f0f0', borderBottom: '1px solid #f0f0f0',
          background: hasData ? (unpaid > 0 ? '#fff1f0' : '#f6ffed') : '#fff',
          borderLeft: isToday ? '3px solid #1677ff' : undefined,
        }}>
          <div style={{ textAlign: 'center',fontSize: 16, fontWeight: isToday ? 700 : 400, color: isToday ? '#1677ff' : '#333' }}>{d}</div>
          {unpaid > 0 && <div style={{textAlign: 'center', fontSize: 16, color: '#e91e63', fontWeight: 600 }}>{formatAmountZhCn(unpaid)}</div>}
          {paid > 0 && <div style={{ textAlign: 'center',fontSize: 16, color: '#00b578' }}>{formatAmountZhCn(paid)}</div>}
        </div>
      );
    }

    return (
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', border: '1px solid #f0f0f0', borderRadius: 6, overflow: 'hidden' }}>
        {cells}
      </div>
    );
  };

  // 带汇总行的表格数据
  const dataWithSummary = useMemo(() => {
    if (data.length === 0) return [];
    const sum: DailyData = {
      dueDate: '汇总',
      dayOfWeek: '',
      totalUnpaid: summary.totalUnpaid,
      totalPaid: summary.totalPaid,
      mortgageUnpaid: data.reduce((s, d) => s + (d.mortgageUnpaid || 0), 0),
      cardUnpaid: data.reduce((s, d) => s + (d.cardUnpaid || 0), 0),
      childList: [],
    };
    return [sum, ...data];
  }, [data, summary]);

  return (
    <Modal
      title={`${monthLabel}资金规划 (${paidLabel})`}
      fullScreen={true}
      headerStyle={{ flexShrink: 0, borderBottom: "1px solid var(--semi-color-border)" }}
      bodyStyle={{ overflow: "auto", flex: 1 }}
      footer={null}
      hasCancel={false}
      onCancel={props.onClose}
      onOk={props.onClose}
      visible={props.visible}
    >
      {/* 摘要卡片 */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
        <div style={{ flex: 1, background: '#fff1f0', borderRadius: 8, padding: '12px 16px', textAlign: 'center' }}>
          <div style={{ fontSize: 22, fontWeight: 'bold', color: '#e91e63' }}>{formatAmountZhCn(summary.totalUnpaid)}</div>
          <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>总待还</div>
        </div>
        <div style={{ flex: 1, background: '#f6ffed', borderRadius: 8, padding: '12px 16px', textAlign: 'center' }}>
          <div style={{ fontSize: 22, fontWeight: 'bold', color: '#00b578' }}>{formatAmountZhCn(summary.totalPaid)}</div>
          <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>总已还</div>
        </div>
        <div style={{ flex: 1, background: '#fff7e6', borderRadius: 8, padding: '12px 16px', textAlign: 'center' }}>
          <div style={{ fontSize: 18, fontWeight: 'bold', color: '#fa8c16' }}>{summary.maxDay ? summary.maxDay.substring(5) : '-'}</div>
          <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>高峰日 ({formatAmountZhCn(summary.maxAmount)})</div>
        </div>
        <div style={{ flex: 1, background: '#e6f7ff', borderRadius: 8, padding: '12px 16px', textAlign: 'center' }}>
          <div style={{ fontSize: 22, fontWeight: 'bold', color: '#1677ff' }}>{formatAmountZhCn(summary.avgUnpaid)}</div>
          <div style={{ fontSize: 12, color: '#999', marginTop: 2 }}>日均待还</div>
        </div>
      </div>

      <Tabs type="line">
        <TabPane tab="日历视图" itemKey="calendar">
          {renderCalendar()}
        </TabPane>
        <TabPane tab="表格视图" itemKey="table">
          <Table
            columns={tableColumns}
            dataSource={dataWithSummary}
            rowKey="dueDate"
            size="small"
            pagination={false}
            loading={loading}
            expandedRowRender={renderExpandedRow}
            onRow={(record: DailyData) => ({
              style: record.dueDate === '汇总' ? { background: '#f0f5ff', fontWeight: 700 } : {},
            })}
          />
        </TabPane>
        
      </Tabs>
    </Modal>
  );
}
